/**
 * e-Barimt: a registration number in, a company name back.
 *
 * **The shell asks when there is one, and the server asks otherwise.** e-Barimt
 * answers this lookup only from inside Mongolia, and `apps/server` runs on
 * Hetzner — so the till is the only part of this system standing in the right
 * country, and the server route it used to call cannot answer from where it now
 * runs. The page cannot ask directly whatever the geography: e-Barimt pins
 * `Access-Control-Allow-Origin` to `https://developer.itc.gov.mn`.
 *
 * Both paths return the same two refusals and are rendered the same way, and
 * the server one is kept rather than deleted because it is what answers in a
 * plain browser and in `pnpm dev` on a machine that *is* in Mongolia — which
 * is where this screen is worked on.
 *
 * **What a failure costs is the name and nothing else.** It is shown here so a
 * customer can see the number they typed is the company they meant, and printed
 * under the number on the slip. What reaches 1C is `iscompany` and `regno`, so
 * a lookup that fails is a company receipt filed on the number alone.
 *
 * **The number is typed on a keypad this component draws.** It was a bare
 * `inputMode="numeric"` field, which on a machine with no keyboard is a field
 * nobody can fill: the panel runs a fullscreen webview under a kiosk lock and
 * Windows' own touch keyboard does not reliably appear for it. So the only
 * customers who could ever take a company receipt were the ones testing it on
 * a laptop. `NumberPad` is the same keys the search box has, at ten digits.
 */
import { useState } from 'react'
import { Button, Notice, Sheet } from '@/components/ui'
import { NumberPad } from '@/components/keyboard'
import { trpc, refusalOf } from '@/api/client'
import { companyLookup, inShell } from '@/shell/native'
import { useRefusal, useT } from '@/i18n'
import { AppliedExtra, PaymentExtra } from './payment-extra'

export interface Company {
  registrationNumber: string
  name: string
  tin?: string
}

/** 1C will not resolve anything shorter, and the field takes no more. */
const REG_MAX = 12
const REG_MIN = 5

const askTheServer = (registrationNumber: string) => trpc.payments.resolveCompany.query({ registrationNumber })

/**
 * The refusal code, from whichever of the two answered.
 *
 * A tRPC refusal carries it in its data and `refusalOf` reads it; the shell
 * rejects with the bare code, because `ebarimt.rs` returns the same two strings
 * `packages/i18n` already has. `refusalText` falls back to an apology for
 * anything it does not recognise, so a shell that rejected with something else
 * — an ACL refusal on an old binary, say — reads as a failure rather than as a
 * wrong number.
 */
export const codeOf = (error: unknown): string | null => (typeof error === 'string' ? error : refusalOf(error))

/** The two things `ebarimt.rs` refuses with. Anything else is not an answer. */
const REFUSALS = new Set(['company-not-found', 'ebarimt-unavailable'])

/**
 * Ask the shell, and fall through to the server unless the shell actually
 * answered.
 *
 * **The fall-through is for the fleet, not for tidiness.** The binary ships a
 * few times a year, so for as long as the rollout takes there are tills where
 * `inShell()` is true and `shell_company_lookup` is not in the capability — and
 * a command the ACL refuses rejects with a message the page has no string for.
 * Without this, those kiosks would answer a company receipt with an apology
 * where today they answer with e-Barimt's own refusal. Any shell-side surprise
 * takes the same route, which is the `maybe` every other native call answers.
 *
 * A real refusal is *not* retried against the server. «No such company» is
 * settled wherever it is heard, and asking Germany for a second opinion on it
 * buys a slower identical answer.
 */
export const resolve = async (registrationNumber: string) => {
  if (!inShell()) return askTheServer(registrationNumber)
  try {
    return await companyLookup(registrationNumber)
  } catch (error) {
    const code = codeOf(error)
    if (code !== null && REFUSALS.has(code)) throw error
    return askTheServer(registrationNumber)
  }
}

export function CompanyReceipt({
  value,
  onChange,
  online
}: {
  value: Company | null
  onChange: (company: Company | null) => void
  online: boolean
}) {
  const t = useT()
  const refusalText = useRefusal()
  const [open, setOpen] = useState(false)
  const [regNo, setRegNo] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const lookup = async () => {
    setBusy(true)
    setProblem(null)
    try {
      const number = regNo.trim()
      const company = await resolve(number)
      onChange({
        registrationNumber: number,
        name: company.name,
        tin: company.tin
      })
      setOpen(false)
      setRegNo('')
    } catch (error) {
      /*
       * The catalogue, not a hardcoded pair.
       *
       * This mapped one code and sent everything else to «something went
       * wrong», so `ebarimt-unavailable` — the customer's number is fine, the
       * service is down — read as an unexplained failure and they retyped a
       * correct number. `useRefusal` is what every other screen uses and it
       * already falls back to an apology for a code it does not know.
       */
      setProblem(refusalText(codeOf(error)))
    } finally {
      setBusy(false)
    }
  }

  if (value !== null) {
    return (
      <AppliedExtra label={t('payment.company')} onRemove={() => onChange(null)}>
        <p className="display-upright truncate text-[length:var(--text-kiosk)]">{value.name}</p>
        <p className="text-[length:var(--text-kiosk-sm)] text-[var(--color-ink-soft)] tabular-nums">
          {value.registrationNumber}
        </p>
      </AppliedExtra>
    )
  }

  return (
    <>
      {/*
        Disabled offline, and it says why. The payment methods beside it have
        carried their reason since they were built (`payment.tsx`'s
        `unavailable()`), and this control was the one exception — a customer
        who wanted a company receipt met a grey button and no account of it.
        The reason is now the *sale* rather than the lookup: in the shell the
        name resolves without the server, but `payByCard` refuses offline and
        nothing here can be filed, so offering the field would be offering a
        receipt for an order that cannot be placed. Same answer the methods
        give.
      */}
      <PaymentExtra
        icon="receipt"
        label={t('payment.company')}
        hint={t('payment.companyHint')}
        unavailable={online ? null : t('payment.offline')}
        onOpen={() => setOpen(true)}
      />

      {/*
        A sheet, not an inline panel. The keypad is 400px of controls and the
        method rows below it are the ones a customer presses next — growing the
        page under them moved the three targets they were reaching for.
      */}
      <Sheet open={open} onClose={() => setOpen(false)} labelledBy="company-title">
        <div className="flex flex-col gap-7 p-10">
          <h2 id="company-title" className="display text-[length:var(--text-kiosk-lg)] uppercase">
            {t('payment.companyRegNo')}
          </h2>

          <p
            aria-live="polite"
            className="display rounded-[var(--radius-card)] border-2 border-[var(--color-line)] bg-[var(--color-paper-sunk)] px-8 py-6 text-[length:var(--text-kiosk-xl)] tabular-nums"
          >
            {regNo.length === 0 ? <span className="text-[var(--color-ink-ghost)]">—</span> : regNo}
          </p>

          <NumberPad
            onKey={(digit) => setRegNo((current) => (current + digit).slice(0, REG_MAX))}
            onBackspace={() => setRegNo((current) => current.slice(0, -1))}
            onClear={() => setRegNo('')}
          />

          {problem !== null && <Notice tone="bad">{problem}</Notice>}

          <div className="flex items-center gap-5">
            <Button variant="text" className="text-[var(--color-ink-faint)]" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              size="large"
              className="ml-auto uppercase"
              disabled={regNo.length < REG_MIN || busy}
              onClick={() => void lookup()}
            >
              {t('payment.companyLookup')}
            </Button>
          </div>
        </div>
      </Sheet>
    </>
  )
}
